import React, { useEffect, useState } from 'react';
import {
    GridCellParams, GridColumnOrderChangeParams, GridColumnResizeParams,
} from "@mui/x-data-grid-pro";
import {
    useGetAddressesQuery,
} from "../../services/grace";
import { Box } from "@mui/material";
import AccountPickerGroup, { AccountPickerGroupValue } from "../../components/widgets/AccountPicker/AccountPickerGroup";
import AddressDialog from "../../components/dialogs/AddressDialog/AddressDialog";
import AddressBookGrid from "../../components/grids/AddressGrid/AddressBookGrid";
import AddressRemoveDialog from '../../components/dialogs/AddressRemoveDialog/AddressRemoveDialog';
import { useAlertDispatch } from '../../context/AlertContext/AlertContext';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import GDGAppBar from "../../components/app/GDGAppBar/GDGAppBar";
import GDGAppBarButton from "../../components/app/GDGAppBar/GDGAppBarButton";
import { useAppDispatch, useAppSelector } from "../../util/hooks";
import { RootState } from "../../util/store";
import { GridColumnDef } from "../../components/grids/grid";
import { setColumnDef } from "../../features/address/addressSlice";

enum PageModes {
    List = 'list',
    Add = 'add',
    Edit = 'Edit',
    ConfirmRemove = 'ConfirmRemove',
}

declare type PageMode = PageModes.List | PageModes.Add | PageModes.Edit | PageModes.ConfirmRemove;

export default function AddressBook(): JSX.Element {
    const dispatch = useAppDispatch();

    const columnDef = useAppSelector((state: RootState) => state.address.columnDef);

    const [pageMode, setPageMode] = useState<PageMode>(PageModes.List);
    const [selectedAccount, setSelectedAccount] = useState<string>("");
    const selectedValues = useAppSelector((state) => state.search.selectedValuesAddressForm);

    const {
        data: addressRows,
        error,
        isError,
        isFetching,
    } = useGetAddressesQuery(selectedAccount, { skip: selectedAccount === "" });

    const alertDispatch = useAlertDispatch();

    useEffect(() => {
        if (isError) {
            const err = error as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to load address data",
                    }
                });
            }
        }
    }, [isError, error, alertDispatch])

    const [removeItems, setRemoveItems] = useState<Array<string>>([]);
    const [selectedAddress, setSelectedAddress] = useState<string>("");

    const handleCellClick = (params: GridCellParams, event: React.MouseEvent) => {
        if (params.row.description === "Default Pick/Del Address") {
            event.stopPropagation();
            return;
        }

        switch (params.colDef.field) {
            case 'remove':
                event.stopPropagation();
                setRemoveItems([params.id.toString()]);
                setPageMode(PageModes.ConfirmRemove);
                break;
            case 'edit':
                event.stopPropagation();
                setPageMode(PageModes.Edit);
                setSelectedAddress(`${params.id}`);
                break;
            default:
                break;
        }
    };

    const handleAddAddress = () => {
        setPageMode(PageModes.Add);
    };

    const handleResetPageMode = () => {
        setRemoveItems([]);
        setSelectedAddress('');
        setPageMode(PageModes.List);
    };

    const [accountValues, setAccountValues] = useState<AccountPickerGroupValue>({
        level1: "",
        level2: "",
        level3: "",
    });

    useEffect(() => {
        if (selectedValues) {
            setAccountValues({
                level1: selectedValues.level1,
                level2: selectedValues.level2,
                level3: selectedValues.level3,
            });
            setSelectedAccount(selectedValues.level3 || selectedValues.level2 || selectedValues.level1 || "");
          }
    },[selectedValues])     

    const handleAccountChange = (value: AccountPickerGroupValue) => {
        setAccountValues({
            level1: value.level1,
            level2: value.level2,
            level3: value.level3,
        });
        setSelectedAccount(value?.level3 || value?.level2 || value?.level1 || "");
    };

    const handleColumnResize = (params: GridColumnResizeParams) => {
        const newColumnDef:GridColumnDef[] = [];
        columnDef.forEach((item) => {
            if (item.field === params.colDef.field) {
                newColumnDef.push({...item, width: params.width});
            } else {
                newColumnDef.push({...item});
            }
        });
        dispatch(setColumnDef(newColumnDef));
    };

    const handleColumnReorder = (params: GridColumnOrderChangeParams) => {
        const newColumnDef = [...columnDef];
        const item = newColumnDef.splice(params.oldIndex, 1);
        newColumnDef.splice(params.targetIndex, 0, item[0])
        dispatch(setColumnDef(newColumnDef));
    };

    return (
        <Box sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <GDGAppBar title='Addresses'>
                <GDGAppBarButton
                    onClick={handleAddAddress}
                    text='+ Add'
                    disabled={!selectedAccount}
                />
            </GDGAppBar>

            <Box>
                <AccountPickerGroup
                    type={"address"}
                    label="Accounts"
                    required
                    value={accountValues}
                    onChange={handleAccountChange}
                />
            </Box>

            {(pageMode === PageModes.Add || pageMode === PageModes.Edit) &&
                <AddressDialog
                    onClose={handleResetPageMode}
                    onSubmit={handleResetPageMode}
                    title={`${pageMode === PageModes.Add ? 'Add new' : 'Edit'} address`}
                    open={true}
                    accountId={selectedAccount}
                    addressId={selectedAddress}
                />
            }

            {pageMode === PageModes.ConfirmRemove
                ? <AddressRemoveDialog
                    text="Are you sure you want to remove address?"
                    removeItems={removeItems}
                    onClose={handleResetPageMode}
                    onSubmit={handleResetPageMode}
                    open={true}
                    accountId={selectedAccount}
                />
                : null
            }

            <AddressBookGrid
                rows={addressRows && !isFetching ? addressRows : []}
                columnDef={columnDef}
                loading={isFetching}
                onCellClick={handleCellClick}
                onColumnResize={handleColumnResize}
                onColumnOrderChange={handleColumnReorder}
            />
        </Box>
    );
}
